import { isTruckingOrganizationLogist } from "helpers/roles";
import { alert } from "services/alert";

export type PrintFormDocumentType = "waybill" | "shippingOrder";

export interface DownloadPrintFormOptions {
    documentId: number;
    documentType: PrintFormDocumentType;
    requestFn: () => Promise<Response>;
    fallbackFileName?: string;
}

/**
 * Match the backend TruckingOrganizationLogists policy for downloading print forms.
 */
export const canDownloadExternalPrintForms = isTruckingOrganizationLogist;

/**
 * Parse file name from Content-Disposition header with fallback support.
 */
export const getFileNameFromContentDisposition = (
    contentDisposition: string | null | undefined,
    fallbackFileName: string,
): string => {
    if (!contentDisposition) {
        return fallbackFileName;
    }

    const utf8Matches = /(?:^|;)\s*filename\*\s*=\s*UTF-8'[^']*'([^;\r\n]*)/i.exec(contentDisposition);
    if (utf8Matches && utf8Matches[1]) {
        try {
            const fileName = decodeURIComponent(utf8Matches[1].trim()).trim();
            if (fileName) {
                return fileName;
            }
        } catch {
            // ignore decoding error and proceed to standard format
        }
    }

    const standardMatches = /(?:^|;)\s*filename\s*=\s*(?:"((?:\\.|[^"\\])*)"|([^;"\r\n]*))/i.exec(contentDisposition);
    const fileName = standardMatches?.[1]?.replace(/\\(.)/g, "$1") ?? standardMatches?.[2];
    return fileName?.trim() || fallbackFileName;
};

/**
 * Download external PDF print form with error handling and browser blob trigger.
 */
export const downloadExternalPrintForm = async ({
    documentId,
    documentType,
    requestFn,
    fallbackFileName,
}: DownloadPrintFormOptions): Promise<boolean> => {
    const isWaybill = documentType === "waybill";
    const documentName = isWaybill ? "ЭТрН" : "ЭЗЗ";
    const defaultFallbackName = isWaybill ? `Waybill_${documentId}.pdf` : `ShippingOrder_${documentId}.pdf`;
    const resolvedFallback = fallbackFileName || defaultFallbackName;

    try {
        const response = await requestFn();
        if (!response) {
            throw new Error("Empty response received");
        }

        if (response.ok) {
            const blob = await response.blob();
            if (!blob || blob.size === 0) {
                throw new Error("Empty file content received");
            }

            const pdfBlob = blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
            const disposition = response.headers.get("Content-Disposition");
            let fileName = getFileNameFromContentDisposition(disposition, resolvedFallback);
            if (!fileName.toLowerCase().endsWith(".pdf")) {
                fileName = `${fileName}.pdf`;
            }

            const objectUrl = window.URL.createObjectURL(pdfBlob);
            const link = document.createElement("a");
            link.href = objectUrl;
            link.download = fileName;
            try {
                document.body.appendChild(link);
                link.click();
            } finally {
                window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 60000);
                document.body.removeChild(link);
            }
            return true;
        }

        if (response.status === 400) {
            const errorText = await response.text();
            alert({
                type: "error",
                title: "Ошибка",
                message: errorText?.trim() || `Не удалось скачать печатную форму ${documentName}`,
            });
            return false;
        }

        if (response.status === 404) {
            alert({
                type: "error",
                title: "Ошибка",
                message: "Не удалось получить печатную форму. Проверьте доступ и наличие документа в Контуре",
            });
            return false;
        }

        alert({
            type: "error",
            title: "Ошибка",
            message: `Не удалось скачать печатную форму ${documentName}`,
        });
        return false;
    } catch {
        alert({
            type: "error",
            title: "Ошибка",
            message: `Не удалось скачать печатную форму ${documentName}`,
        });
        return false;
    }
};
